import { useMemo } from 'react'
import type { FC } from 'react'
import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import type { Block, Document as RichTextDocument } from '@contentful/rich-text-types'

import { AppearanceProvider } from '@segment/matcha/foundations/AppearanceContext'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import NavigationV2 from '@segment/templates/Navigation2'
import GettingStarted from '@segment/templates/GettingStarted'
import Footer from '@segment/templates/Footer'

import { useSingleRecipeQuery } from '@segment/api/routes/recipes/queries'

import useRichTextTableOfContent, { getNodeText } from '@segment/utils/useRichTextTableOfContent'

import Layout from '@segment/next-shared/layout'

import Hero from './components/Hero'
import Sidebar from './components/Sidebar'
import Content from './components/Content'

interface RecipesSinglePageViewProps {
  slug: string
}

const RecipesSinglePageView: FC<RecipesSinglePageViewProps> = ({ slug }) => {
  const theme = useTheme()
  const { data } = useSingleRecipeQuery({ args: [slug] })
  if (!data) return null

  const { content, title, description, metaDescription, ogImage, authors, requirements, ctaAdTitle, industries } = data

  const tableOfContentState = useRichTextTableOfContent(content as RichTextDocument)
  const contentHeadingsOptions = useMemo(
    () =>
      tableOfContentState.getMenuLevelItems(0).map(([node, id]) => ({ text: getNodeText(node as Block), value: id })),
    [tableOfContentState]
  )

  return (
    <AppearanceProvider appearance="twilioLight">
      <Layout
        pageTitle={title}
        pageDescription={metaDescription}
        pageOG={ogImage?.src}
        themeColor={theme.palette.neutrals.white[100]}
        bodyBackground={theme.palette.neutrals.white[100]}
      >
        <NavigationV2 />
        <Hero
          heading={title}
          description={description}
          authors={authors?.map((author) => ({
            name: author.name,
            avatar: author.headshot.src,
            width: author.headshot.width,
            height: author.headshot.height,
          }))}
          requirements={requirements}
          ctaAdTitle={ctaAdTitle}
          badges={industries}
        />
        <Main>
          <SectionBase variant="sidebar" sidebarSize="small" sticky={true}>
            <Section.Sidebar as="aside">
              <Section.Sticky>
                <Sidebar id="sidebar" data={contentHeadingsOptions} />
              </Section.Sticky>
            </Section.Sidebar>
            <StyledSectionContents>
              <Content data={content as RichTextDocument} tableOfContentState={tableOfContentState} />
            </StyledSectionContents>
          </SectionBase>
          <GettingStarted />
        </Main>
        <Footer />
      </Layout>
    </AppearanceProvider>
  )
}

const StyledSectionContents = styled(Section.Contents)`
  overflow: hidden;
`

export default RecipesSinglePageView

const Main = styled.main``
